"use client";

import { SVGIconProps } from "@faire/slate-icons/types";
import { viewport } from "@faire/slate/lib/mediaQuery";
import { componentCls } from "@faire/slate/lib/tracking";
import {
  TOAST_TIMEOUT_MEDIUM_MS,
  TOAST_TIMEOUT_LARGE_MS,
} from "@faire/slate/private/toast/consts";
import { ToastBase } from "@faire/slate/private/toast/ToastBase";
import { ToastAccessory } from "@faire/slate/private/toast/types";
import { delay } from "@faire/typescript/delay";
import React from "react";
import { Id, toast, ToastContentProps } from "react-toastify";

import {
  TOAST_CONTAINER_DESKTOP_ID,
  TOAST_CONTAINER_MOBILE_ID,
} from "./consts";

// EXCEPTION — do not copy: Toast is an imperative API (show/hide functions), not a rendered
// component, so its props type must be exported for callers. For every other component, IProps
// is local and never exported — types consumers need go in private/<name>/types.ts instead.
export type IProps = {
  /**
   * Optional call-to-action label, shown on its own row below the message.
   *
   * When provided, the toast will be dismissed automatically after 6 seconds,
   * otherwise it will be dismissed after 4 seconds.
   */
  cta?: string;

  /**
   * The content of the toast.
   */
  description: string;

  /**
   * Leading accessory. Omit for none (Figma accessory=false).
   */
  accessory?: ToastAccessory;

  /**
   * @deprecated Use `accessory={{ type: "icon", Icon }}` instead.
   * Still supported; equivalent to an icon accessory when `accessory` is omitted.
   */
  Icon?: React.FC<SVGIconProps>;

  /**
   * **web**
   * The callback invoked when the CTA is actioned.
   */
  onCtaClick?: () => void;

  /**
   * **web**
   * The callback invoked when the toast is closed or the CTA is actioned.
   *
   * @note Toast always renders with a dismiss button, even if onDismiss is omitted.
   */
  onDismiss?: () => void;

  // DO NOT ADD any new prop without aligning with the Slate team first
};

/**
 * Toasts contain information directly related to an action performed.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Toast-2242efb5c25a807ca093e3a5722ab756 | Slate/Toast}
 */
export const Toast = {
  /**
   * Shows a toast.
   * @trackfunction
   *
   * @param props - {@link IProps} of the toast to be shown.
   * @returns The toast Id.
   */
  create: createToast,
  /**
   * Closes a toast.
   * @trackfunction
   *
   * @param id - The Id of the toast to be closed.
   */
  dismiss: (id?: Id) => toast.dismiss(id),
};

function createToast({
  onDismiss: onClose,
  description,
  accessory,
  Icon,
  cta,
  onCtaClick,
}: IProps): Id {
  const isMobile = viewport.sm()?.matches;

  const containerId = isMobile
    ? TOAST_CONTAINER_MOBILE_ID
    : TOAST_CONTAINER_DESKTOP_ID;

  const sendToast = () => {
    toastId = nextId++;
    // oxlint-disable-next-line @faire/no-inline-hoc-fc
    return toast((props) => <ToastContent {...props} />, {
      onClose,
      data: { description, accessory, Icon, cta, onCtaClick },
      className: componentCls("fs-toast"),
      containerId,
      toastId,
      autoClose: cta ? TOAST_TIMEOUT_LARGE_MS : TOAST_TIMEOUT_MEDIUM_MS,
    });
  };

  // We allow only one toast to be shown at a time
  if (toast.isActive(toastId)) {
    // We need to wait for the toast to be removed before sending a new one
    // otherwise the animation gets weird because the previous toast is still visible
    const listener = toast.onChange((event) => {
      if (event.status === "removed" && event.id === toastId) {
        listener();
        void delay(0).then(() => sendToast());
      }
    });
    toast.dismiss(toastId);

    return nextId;
  }

  return sendToast();
}

const ToastContent = ({
  closeToast,
  data: { description, accessory, Icon, cta, onCtaClick },
}: ToastContentProps<IProps>) => {
  return (
    <ToastBase
      description={description}
      accessory={accessory}
      Icon={Icon}
      onDismiss={closeToast}
      cta={cta}
      onCtaClick={onCtaClick}
      data-component-name="Slate/Toast"
    />
  );
};

let toastId: number,
  nextId: number = 0;
